import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { appSetting } from 'config/appSettings';
import { AuthService } from 'modules/app/services/auth.service';
import { MessageService } from 'modules/app/services/message.service';
import { TabbingOrderService } from 'modules/foc/services/tabbing-order.service';

declare var $:any;

type ToolTipsAvailable_Selection = "SELECTION_TOOLS";
type ToolTipsAvailable_Notes = "NOTES";
type ToolTipsAvailable_PDZ = "PDZ";
type ToolTipsAvailable_PDF = "PDF";
type ToolTipsAvailable_URL = "URL";
type TooltipsAvailable_NotesLinks = "NOTES_LINKS";

type TooltipsAvailable = ToolTipsAvailable_Selection | ToolTipsAvailable_Notes | ToolTipsAvailable_PDZ | ToolTipsAvailable_PDF | ToolTipsAvailable_URL | TooltipsAvailable_NotesLinks;

@Component({
    selector: 'app-tip-modal',
    templateUrl: './tip-modal.template.html',
 	standalone: false,
    styleUrls: ['./tip-modal.style.scss']
})
export class TipModal implements OnInit, OnDestroy {

    @Input() public sidebarPosition = 'left';
    @Input() public baseType: any = 'SELECTION_TOOLS';

    public selectedTools = [];
    public isCookieBannerVisible: boolean = false;

    private localStorageKey: string;
    private tipsPreferencesMap = {};
    private cookieStatusSubscriber: any;
    private ignoreShowTipModal: boolean = false;

    public content: string = '';

    constructor(
        private authService: AuthService,
        private messageService: MessageService,
        public tabbingOrderService: TabbingOrderService,
        private readonly el: ElementRef
    ) { }

    ngOnInit() {

        this.updateTipPreference();

        // Check if cookie banner is visible
        const cookieBanner = document.getElementsByClassName('cookies-banner');
        this.isCookieBannerVisible = !!(cookieBanner && cookieBanner[0]);

        this.cookieStatusSubscriber = this.messageService.getChannelObservable('cookieStatus').subscribe((res: any) => {
            this.isCookieBannerVisible = res.data && res.data.enabled;
        });

        if (this.baseType == 'SELECTION_TOOLS' || this.baseType == 'NOTES_LINKS') {
            this.showTipModal(this.baseType);
        }
    }
    /**
    * Listens for the Escape key press at the document level.
    * If a tooltip with the class `.tip-modal` and `.PDF` is found,
    * it calls the `hideTipModal` method to close it.
    */
    @HostListener('document:keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent) {
        // Find the tooltip element inside this component
        const tooltip = this.el.nativeElement.querySelector('.tip-modal .PDF');

        // If a tooltip is present, close it
        if (tooltip) {
            this.hideTipModal("PDF", false , true);
        }
    }

    public hideTipModal(selectedTool: TooltipsAvailable, saveToLS: boolean = false, closedByUser: boolean = false): boolean {
        const index = this.selectedTools.findIndex(v => v === selectedTool);

        this.tabbingOrderService.tipModal?.onClose(this.tabbingOrderService.tipModal);

        if (index === -1) return false;

        $('#tipModal-' + selectedTool).animate({ height: 0, opacity: 0 }, 200, 'linear', () => {
			// On animation completion, remove item from the array
            this.selectedTools.splice(index, 1);
        });

        if (saveToLS) {
            this.saveTipPreference(selectedTool);
        }

        if (selectedTool === 'PDZ') {
            window.sessionStorage.setItem('showPDZTipModal', 'false');
        }

        if (selectedTool === 'PDF') {
            this.resetPdfHotspotTabIndex();
        }

        if (closedByUser) {
            this.ignoreShowTipModal = true;
        }

        return true;
    }

    private saveTipPreference(selectedTool: TooltipsAvailable) {
        this.tipsPreferencesMap[selectedTool] = true;
        window.localStorage.setItem(this.localStorageKey, JSON.stringify(this.tipsPreferencesMap));
    }

    private resetPdfHotspotTabIndex() {
        const pdfHotspot = document.querySelector('[data-selected-pdf="true"]');
        if (pdfHotspot) {
        	// Find the first child div inside the selected pdfHotspot
            const childDiv = pdfHotspot.querySelector('div');
            if (childDiv) {
            	// Reset tabindex of the child div to the default tabindex value
                childDiv.setAttribute('tabindex', this.tabbingOrderService?.flattenedTabOrderMap?.hotspots?.currentIndex);
            }
            pdfHotspot.removeAttribute('data-selected-pdf');
        }
    }

    public showTipModal(tool: TooltipsAvailable, position=null, text=''): boolean {

        if(this.ignoreShowTipModal && tool === 'PDF') { 
            return false;
        }
        
        this.updateTipPreference();

        // Check the tipsPreferencesMap to check if the user has asked to never show the tip
        if (this.tipsPreferencesMap[tool]) {
            return false;
        }
        // Don't show the tip if it is already visible
        if (this.selectedTools.indexOf(tool) > -1) {
            return false;
        }

        if(tool==='PDZ'){
            let showPDZTipModal = window.sessionStorage.getItem('showPDZTipModal');
            if(showPDZTipModal && !JSON.parse(showPDZTipModal)){
                return false;
            }
        }

        // TODO: Combine the baseType of PDZ and PDF to avoid confusion
        if(tool==='PDF'){
            this.baseType = 'PDF';
        }
        
        if(tool==='URL' && !this.selectedTools.includes('NOTES')){
            this.baseType = 'URL';
        }

        this.selectedTools.push(tool);
        setTimeout( () => {
            if (position) {
                if(tool === 'PDZ') {
                    $(".tip-modal.PDZ").css(position);
                } else if(tool === 'PDF') {
                    $(".tip-modal.PDF").css(position);
                }
                else if(tool === 'URL') {
                    $(".tip-modal.URL").css(position);
                    this.content = text;
                }
            }
            $('#tipModal-' + tool).css({opacity:1, transform: 'translateX(0)', '-webkit-transform': 'translateX(0)'});
            let tipModalContainer = document.querySelector('.tip-modal-container.' + tool );
            if(tipModalContainer && (tool == 'NOTES' || tool =='NOTES_LINKS')) {
                tipModalContainer.scrollIntoView({
                    behavior: "smooth", block: "nearest", inline: "nearest"
                });
            }
        });
        return true;
    }

    ngOnDestroy() {
        if (this.cookieStatusSubscriber) {
            this.cookieStatusSubscriber.unsubscribe();
        }
    }

    updateTipPreference() {
        const userData = this.authService.getUserData();
        this.localStorageKey = appSetting.localStorage.tipsPreferences(userData.extUserId);
        if (window.localStorage.getItem(this.localStorageKey)) {
            this.tipsPreferencesMap = JSON.parse(window.localStorage.getItem(this.localStorageKey));
        }
    }
}